import { ScoreDTO } from '../../services/apiClient';
import { TableAction } from '../common/Table';
import { Check, Clock, Edit, Mail, MessageSquareText, RotateCcw, Search, Star, Trash2, User, UserCog } from 'lucide-react';
import { ScoreStatusConstants } from '../../constants/ScoreStatusConstants';

interface ScoreActionHandlers {
  onViewMemberDetails: (score: ScoreDTO) => void;
  onView: (score: ScoreDTO) => void;
  onEdit: (score: ScoreDTO) => void;
  onDelete: (score: ScoreDTO) => void;
  onSendEmail: (member: ScoreDTO) => void;
  onViewAllEmails: (member: ScoreDTO) => void;
  onViewRecordEmails: (member: ScoreDTO) => void;
  onRestore: (score: ScoreDTO) => Promise<void>;
  onApprove: (score: ScoreDTO) => void;
  onChangeStatus: (score: ScoreDTO) => void;
  onImpersonateMemberLogin: (score: ScoreDTO) => void;
  onAddNote: (score: ScoreDTO) => void;
  onViewNoteHistory: (score: ScoreDTO) => void;
}

const createScoreActions =
  (handlers: ScoreActionHandlers) =>
    (row: ScoreDTO): TableAction[] => {
      const actions: TableAction[] = [];

      if (!row.isDeleted) {
        actions.push({
          label: 'View Member',
          onClick: () => handlers.onViewMemberDetails(row),
          icon: <User className="w-4 h-4" />,
        });
      }

      // Check if status is locked (Approved, Approved with Feedback, or Declined)
      const isStatusLocked = [
        ScoreStatusConstants.APPROVED.toLowerCase(),
        ScoreStatusConstants.APPROVED_WITH_FEEDBACK.toLowerCase(),
        ScoreStatusConstants.OUTSTANDING.toLowerCase(),
        ScoreStatusConstants.DECLINED.toLowerCase(),
      ].includes(row.scoreStatusId?.toLowerCase() || '');

      if (!row.isDeleted && handlers.onView) {
        actions.push({
          label: 'View Score',
          onClick: () => handlers.onView(row),
          icon: <Search className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && row.member?.userID && handlers.onImpersonateMemberLogin) {
        actions.push({
          label: 'Login as Member',
          onClick: () => handlers.onImpersonateMemberLogin(row),
          icon: <UserCog className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to login as this member?',
        });
      }

      if (!row.isDeleted && handlers.onSendEmail) {
        actions.push({
          label: 'Send Email',
          onClick: () => handlers.onSendEmail(row),
          icon: <Mail className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && handlers.onViewAllEmails) {
        actions.push({
          label: 'Messages(All)',
          onClick: () => handlers.onViewAllEmails(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && handlers.onViewRecordEmails) {
        actions.push({
          label: 'Messages(Record)',
          onClick: () => handlers.onViewRecordEmails(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && handlers.onAddNote) {
        actions.push({
          label: 'Create note',
          onClick: () => handlers.onAddNote(row),
          icon: <MessageSquareText className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && handlers.onViewNoteHistory) {
        actions.push({
          label: 'Notes History',
          onClick: () => handlers.onViewNoteHistory(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && handlers.onApprove) {
        actions.push({
          label: 'Approve',
          onClick: () => handlers.onApprove(row),
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600 hover:text-green-700',
        });
      }

      if (!row.isDeleted && handlers.onChangeStatus) {
        actions.push({
          label: 'Change Status',
          onClick: () => handlers.onChangeStatus(row),
          icon: <Star className="w-4 h-4" />,
          className: 'text-orange-600 hover:text-orange-700',
        });
      }



      if (row.isDeleted && handlers.onRestore) {
        actions.push({
          label: 'Restore Deleted',
          onClick: () => handlers.onRestore(row),
          icon: <RotateCcw className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to restore this score?',
          className: 'text-green-600 hover:text-green-700',
        });
      } else if (!isStatusLocked && handlers.onDelete) {
        actions.push({
          label: 'Delete Score',
          onClick: () => handlers.onDelete(row),
          icon: <Trash2 className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to delete this score?',
          className: 'text-red-600 hover:text-red-700',
        });
      }

      return actions;
    };

export default createScoreActions;
